import { Slot } from "@radix-ui/react-slot";
import { cn } from "@sylviq/ui-react-web/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import type { ButtonHTMLAttributes } from "react";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] disabled:pointer-events-none disabled:opacity-45",
  {
    variants: {
      variant: {
        primary: "bg-[var(--action)] text-[var(--action-foreground)] shadow-sm hover:brightness-110",
        secondary: "bg-[var(--muted)] text-[var(--foreground)] hover:bg-[var(--muted-strong)]",
        outline: "border border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--muted)]",
        ghost: "hover:bg-[var(--muted)]",
        destructive: "bg-[var(--danger)] text-[var(--danger-foreground)] hover:brightness-110",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-5 text-base",
        icon: "size-10 p-0",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({ asChild, className, variant, size, ...props }: ButtonProps) {
  const Component = asChild ? Slot : "button";
  return <Component className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}
